import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import Checkmark from '@/assets/icons/checkmark';
import { LineSeparator } from '@/components/LineSeparator';
import { ScrollableScreen } from '@/components/ScrollableScreen';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { AccountStackParamList } from '@/navigation/types';

import { CancelWithdrawalAction } from '../components/CancelWithdrawalAction';
import { CurrencyAmount } from '../components/CurrencyAmount';
import { TransactionRequestStatus } from '../components/TransactionRequestStatus';
import { useTransactionDetails } from '../hooks/use-transaction-details';
import { formatTransaction } from '../utils/formatters';
import { getWithdrawalCancellationState } from '../utils/withdrawal-cancellation';

type ScreenProps = NativeStackScreenProps<AccountStackParamList, 'TransactionDetails'>;

export const TransactionDetailsScreen = ({ route }: ScreenProps) => {
    const { userWalletId } = route.params;
    const { data: transaction } = useTransactionDetails(userWalletId);
    const hasSocialWallet = useHasSocialWallet();
    const { t } = useTranslation(['transaction_details', 'wallet']);

    const transactionDetails = useMemo(() => formatTransaction(transaction), [transaction]);
    const cancellationState = useMemo(() => getWithdrawalCancellationState(transaction), [transaction]);

    if (!transaction || !transactionDetails) {
        return null;
    }

    const depositText = hasSocialWallet ? t('wallet:purchase') : t('wallet:deposit');
    // `currency: true` rows render with an inline currency icon (CurrencyAmount); the rest are plain text.
    const rows = [
        { label: t('initiated'), key: 'initiated' },
        { label: t('status'), key: 'status' },
        { label: t('cost'), key: 'cost' },
        { label: t('before_total_balance'), key: 'beforeBalance', currency: true },
        { label: t('after_total_balance'), key: 'afterBalance', currency: true },
        { label: t('from'), key: 'from' },
        { label: t('to'), key: 'to' },
        { label: t('daily_limit_after', { type: depositText }), key: 'dailyDailyDepositLimitAfter', currency: true },
        { label: t('weekly_limit_after', { type: depositText }), key: 'weeklyDepositLimitAfter', currency: true },
        { label: t('monthly_limit_after', { type: depositText }), key: 'monthlyDepositLimitAfter', currency: true },
        { label: t('transaction_id'), key: 'transactionId' },
    ] as const;

    return (
        <ScrollableScreen
            screenNavBarProps={{ title: 'Transaction Details' }}
            scrollViewProps={{ style: { paddingHorizontal: 0 } }}
        >
            <MaxWidthWrapper paddingHorizontal={'s16'}>
                <Box mt="s8" mb="s4">
                    {/* Figma "Display/Small" header is 40px icon + 40px line-height; the app's
                        displaySmall token is 44, so we pin lineHeight to keep the number centered on the icon. */}
                    <CurrencyAmount
                        currency={transactionDetails.displayCurrency}
                        amount={transactionDetails.header}
                        iconSize={40}
                        gap="s8"
                        variant="displaySmall"
                        lineHeight={40}
                        textDecorationLine={transactionDetails.isFailed ? 'line-through' : undefined}
                        color={transactionDetails.isFailed ? ds.colorToken.text.secondary : undefined}
                    />
                </Box>
                <Text variant="bodyMedium" mb="s16">
                    {transactionDetails.subText}
                </Text>
                <Box mb="s40">
                    {rows.map(row => {
                        const { label, key } = row;
                        const value = transactionDetails[key];
                        if (!value) {
                            return null;
                        }
                        return (
                            <View key={label}>
                                <Text color="neutral300" variant="bodySmall" mt="s16">
                                    {label}
                                </Text>
                                {key === 'status' && transactionDetails.showStatusStepper ? (
                                    <TransactionRequestStatus transaction={transactionDetails} />
                                ) : key === 'status' && transactionDetails.isCancelled ? (
                                    <Row alignItems="center" gap="s8" mb="s16">
                                        <Checkmark width={16} height={16} color={ds.colorValue.icon.success} />
                                        <Text variant="bodyMedium" testID={`txnDetails-${key}`}>
                                            {value}
                                        </Text>
                                    </Row>
                                ) : 'currency' in row ? (
                                    <Box mb="s16">
                                        <CurrencyAmount
                                            currency={transactionDetails.displayCurrency}
                                            amount={value}
                                            variant="bodyMedium"
                                            testID={`txnDetails-${key}`}
                                        />
                                    </Box>
                                ) : (
                                    <Text variant="bodyMedium" mb="s16" testID={`txnDetails-${key}`}>
                                        {value}
                                    </Text>
                                )}
                                {/* Kept mounted for every withdrawal so the "cannot be cancelled" banner survives
                                    the details refetch after a refused cancel, even when the refetched withdrawal
                                    has no cancellation state left (e.g. it was paid out in the meantime). */}
                                {key === 'initiated' && transaction.action === 'withdrawal' ? (
                                    <CancelWithdrawalAction userWalletId={userWalletId} state={cancellationState} />
                                ) : null}
                                <LineSeparator />
                            </View>
                        );
                    })}
                </Box>
            </MaxWidthWrapper>
        </ScrollableScreen>
    );
};
